import { constants } from "node:fs";
import { access as fsAccess, stat as fsStat } from "node:fs/promises";
import { constants as osConstants } from "node:os";
import { resolve as resolvePath } from "node:path";
import type { AgentTool } from "@earendil-works/pi-agent-core";
import { Container, Text } from "@earendil-works/pi-tui";
import { spawn } from "child_process";
import { type Static, Type } from "typebox";
import { APP_NAME } from "../../config.js";
import { theme } from "../../modes/interactive/theme/theme.js";
import { createChildProcessEnvironment, waitForChildProcess } from "../../utils/child-process.ts";
import {
	getShellConfig,
	getShellEnv,
	killProcessTree,
	trackDetachedChildPid,
	untrackDetachedChildPid,
} from "../../utils/shell.ts";
import type { BashResult } from "../bash-executor.ts";
import type { ExtensionContext, ToolDefinition, ToolRenderResultOptions } from "../extensions/types.ts";
import type { WaitPolicy } from "../tasks/contracts.js";
import {
	type BashInterceptorRule,
	checkBashInterceptionCandidates,
	DEFAULT_BASH_INTERCEPTOR_RULES,
} from "./bash-interceptor.ts";
import { stripLeadingCdCommand } from "./bash-leading-cd.ts";
import {
	executeNativePty,
	executeSupervisedCommand,
	type SupervisedCommandOwner,
	type SupervisedCommandResult,
	validateBashWait,
	waitForSupervisedCommand,
} from "./bash-pty-native.js";
import { applyBashSessionEnvironment, snapshotBashSessionEnvironment } from "./bash-session-environment.ts";
import { OutputAccumulator } from "./output-accumulator.ts";
import {
	getTextOutput,
	invalidArgText,
	startToolTimer,
	str,
	type ToolTimerState,
	toolTimerText,
} from "./render-utils.ts";
import { expandShellInternalUrls, type InternalResourceContext } from "./resource-selectors.ts";
import { invalidateNativeSearchCache } from "./search-native.ts";
import { addShellOutputPreview, formatTruncationNotice, rebuildShellWaitResult } from "./shell-task-render.ts";
import { wrapToolDefinition } from "./tool-definition-wrapper.ts";
import { DEFAULT_MAX_BYTES, formatSize, type TruncationResult } from "./truncate.ts";

const STRUCTURED_OUTPUT_MAX_BYTES = 1024 * 1024;
const bashOutputSchema = Type.Object({
	output: Type.String({
		description: "Combined stdout and stderr, up to 1 MiB, keeping the first and last 512 KiB when longer.",
	}),
	truncated: Type.Boolean(),
	full_output_path: Type.Optional(Type.String()),
	exit_code: Type.Number(),
	wall_time_seconds: Type.Number(),
});

const envSchema = Type.Unsafe<Record<string, string>>({
	type: "object",
	description: "Environment variables to add or override.",
	additionalProperties: { type: "string" },
	propertyNames: { pattern: "^[A-Za-z_][A-Za-z0-9_]*$" },
});
const bashBaseSchema = Type.Object(
	{
		command: Type.String({ description: "Shell command to execute." }),
		env: Type.Optional(envSchema),
		timeout: Type.Optional(Type.Number({ description: "Timeout in seconds." })),
		wait: Type.Optional(
			Type.Unsafe<WaitPolicy>({
				type: "object",
				properties: {
					kind: { type: "string", enum: ["background", "foreground"] },
					budgetMs: {
						type: "number",
						minimum: 0,
						description: "Foreground observation budget in milliseconds; only valid with foreground.",
					},
				},
				required: ["kind"],
				additionalProperties: false,
				description:
					"Observation policy, independent of execution timeout. Omit for owner-configured automatic yield (default 10s).",
			}),
		),
		cwd: Type.Optional(Type.String({ description: "Working directory for the command." })),
		pty: Type.Optional(Type.Boolean({ description: "Run with PTY handling." })),
	},
	{ additionalProperties: false },
);
type ShellCommandInput = Static<typeof bashBaseSchema> & { action?: never; id?: never; budgetMs?: never };
type ShellWaitInput = { action: "wait"; id: string; budgetMs?: number } & Partial<
	Record<keyof Static<typeof bashBaseSchema>, never>
>;
const bashSchema = Type.Unsafe<ShellCommandInput | ShellWaitInput>({
	...bashBaseSchema,
	properties: {
		...bashBaseSchema.properties,
		action: {
			type: "string",
			enum: ["wait"],
			description: "Observe an existing asynchronous task without executing a command.",
		},
		id: { type: "string", description: "Existing task ID owned by this session." },
		budgetMs: {
			type: "number",
			minimum: 0,
			description: "Observation budget in milliseconds; omitted uses owner policy.",
		},
	},
	required: [],
});
const shellCommandKeys = Object.keys(bashBaseSchema.properties);

function prepareShellInput(args: unknown): BashToolInput {
	if (typeof args !== "object" || args === null || Array.isArray(args)) return args as BashToolInput;
	const has = (key: string) => Object.hasOwn(args, key);
	if (has("action")) {
		if (!has("id") || shellCommandKeys.some(has))
			throw new Error("Invalid shell wait: expected action wait, id, and optional finite non-negative budgetMs");
	} else if (!has("command") || has("id") || has("budgetMs")) {
		throw new Error("Invalid shell command: command is required; id and budgetMs require action wait");
	}
	return args as BashToolInput;
}
export const bashToolSystemPromptContribution = Object.freeze({
	snippet: "Execute a shell command.",
	guidelines: Object.freeze([
		"You can inspect ATOMIC_* or PI_* environment variables for current model and session details.",
		'Use { action: "wait", id: taskId } to observe an existing shell task with the owner policy. Avoid repeated short polls; await completion notifications when no dependent work is blocked. Never relaunch a yielded command. Waiting does not extend execution timeout or owner lifetime.',
		"For ordinary commands, omit wait to use the owner's observation policy (normally 10s). Do not routinely request one-second waits: premature yields add tool calls without making the command faster. Use a short budget only for a concrete responsiveness need, or explicit background observation when you have independent work to do. Explicit budgets, including zero for polling, remain available.",
		"Choose foreground or background shell observation as needed without asking the user each time. Background yields after admission; foreground waits for its optional budget then the same command continues in background. Omitted wait uses owner-configured automatic background yield (default 10s). Observation never changes the execution timeout, which stops the command. Background requires a supported task owner; unbound foreground waits until completion regardless of budget.",
	] as const),
} as const);
export type BashToolInput = Static<typeof bashSchema>;
export interface BashToolDetails {
	truncation?: TruncationResult;
	fullOutputPath?: string;
	exitCode?: number | null;
	observation?: import("../tasks/contracts.js").WaitOutcome;
	timeoutSeconds?: number;
	requestedTimeoutSeconds?: number;
	wallTimeMs?: number;
}
const DEFAULT_TIMEOUT_SECONDS = 300,
	MAX_TIMEOUT_SECONDS = 3600;
export function validateExplicitTimeoutSeconds(timeout: number): void {
	if (!Number.isFinite(timeout) || timeout <= 0 || timeout > MAX_TIMEOUT_SECONDS)
		throw new Error(
			`Invalid timeout ${String(timeout)}: timeout must be a finite number greater than 0 and no more than ${MAX_TIMEOUT_SECONDS} seconds`,
		);
}
function normalizeTimeoutSeconds(timeout: number | undefined): number {
	if (timeout === undefined) return DEFAULT_TIMEOUT_SECONDS;
	validateExplicitTimeoutSeconds(timeout);
	return Math.max(1, Math.floor(timeout));
}
export type BashOutputChannel = "stdout" | "stderr";
export interface BashOperations {
	exec: (
		command: string,
		cwd: string,
		options: {
			onData: (data: Buffer, channel?: BashOutputChannel) => void;
			signal?: AbortSignal;
			timeout?: number;
			wait?: WaitPolicy;
			env?: NodeJS.ProcessEnv;
			pty?: boolean;
		},
	) => Promise<SupervisedCommandResult>;
}
export function createLocalBashOperations(options?: {
	shellPath?: string;
	taskOwner?: SupervisedCommandOwner;
}): BashOperations {
	return {
		exec: async (command, cwd, { onData, signal, timeout, wait, env, pty }) => {
			validateBashWait(wait, !!options?.taskOwner);
			if (timeout !== undefined) validateExplicitTimeoutSeconds(timeout);
			if (pty && process.env.PI_NO_PTY !== "1" && process.env.ATOMIC_NO_PTY !== "1") {
				try {
					return await executeNativePty(command, cwd, {
						onData: (data) => onData(data, "stdout"),
						signal,
						timeout,
						wait,
						env,
						shellPath: options?.shellPath,
						taskOwner: options?.taskOwner,
					});
				} catch (error) {
					if (options?.taskOwner) throw error;
					const message = String(error instanceof Error ? error.message : error);
					if (!message.includes("Native PTY") && !message.includes("PtySession")) throw error;
				}
			}
			const shellConfig = getShellConfig(options?.shellPath);
			try {
				const cwdStat = await fsStat(cwd);
				if (!cwdStat.isDirectory()) throw new Error(`Working directory is not a directory: ${cwd}`);
				await fsAccess(cwd, constants.F_OK);
			} catch (error) {
				if (error instanceof Error && error.message.startsWith("Working directory is not a directory")) throw error;
				throw new Error(`Working directory does not exist: ${cwd}\nCannot execute bash commands.`);
			}
			if (signal?.aborted) throw new Error("aborted");
			if (options?.taskOwner)
				return executeSupervisedCommand(
					command,
					cwd,
					{ onData, signal, timeout, wait, env, shellPath: options.shellPath, taskOwner: options.taskOwner },
					false,
				);
			const commandFromStdin = shellConfig.commandTransport === "stdin";
			const child = spawn(shellConfig.shell, commandFromStdin ? shellConfig.args : [...shellConfig.args, command], {
				cwd,
				detached: process.platform !== "win32",
				env: createChildProcessEnvironment(undefined, env ?? getShellEnv()),
				stdio: [commandFromStdin ? "pipe" : "ignore", "pipe", "pipe"],
				windowsHide: true,
			});
			if (commandFromStdin) {
				child.stdin?.on("error", () => {});
				child.stdin?.end(command);
			}
			if (child.pid) trackDetachedChildPid(child.pid);
			let timedOut = false;
			let timeoutHandle: NodeJS.Timeout | undefined;
			const onAbort = () => {
				if (child.pid) killProcessTree(child.pid);
			};
			try {
				if (timeout !== undefined && timeout > 0) {
					timeoutHandle = setTimeout(() => {
						timedOut = true;
						if (child.pid) killProcessTree(child.pid);
					}, timeout * 1000);
				}
				child.stdout?.on("data", (data: Buffer) => onData(data, "stdout"));
				child.stderr?.on("data", (data: Buffer) => onData(data, "stderr"));
				if (signal) {
					if (signal.aborted) onAbort();
					else signal.addEventListener("abort", onAbort, { once: true });
				}
				const exitCode = await waitForChildProcess(child);
				if (signal?.aborted) throw new Error("aborted");
				if (timedOut) {
					throw new Error(`timeout:${timeout}`);
				}
				const signalCode = child.signalCode;
				return { exitCode: exitCode ?? (signalCode ? 128 + (osConstants.signals[signalCode] ?? 0) : 1) };
			} finally {
				if (child.pid) untrackDetachedChildPid(child.pid);
				if (timeoutHandle) clearTimeout(timeoutHandle);
				if (signal) signal.removeEventListener("abort", onAbort);
			}
		},
	};
}
export interface BashSpawnContext {
	command: string;
	cwd: string;
	env: NodeJS.ProcessEnv;
}
export type BashSpawnHook = (context: BashSpawnContext) => BashSpawnContext;
function resolveSpawnContext(
	command: string,
	cwd: string,
	sessionEnvironment: NodeJS.ProcessEnv,
	spawnHook?: BashSpawnHook,
): BashSpawnContext {
	const baseContext: BashSpawnContext = { command, cwd, env: { ...getShellEnv() } };
	applyBashSessionEnvironment(baseContext.env, sessionEnvironment);
	return spawnHook ? spawnHook(baseContext) : baseContext;
}
export interface BashInterceptorResult {
	operations?: BashOperations;
	result?: BashResult;
}
export type BashInterceptor = (
	context: BashSpawnContext,
) => Promise<BashInterceptorResult | undefined> | BashInterceptorResult | undefined;
export interface BashToolOptions {
	operations?: BashOperations;
	/** Trusted host ownership; never supplied by model arguments. */
	taskOwner?: SupervisedCommandOwner;
	/** Expose the execution-time Atomic session snapshot and PI compatibility aliases. Default: true. */
	exposeSessionEnvironment?: boolean;
	/** Prefix prepended to every shell command before execution. */
	commandPrefix?: string;
	/** Override shell executable resolution for local bash operations. */
	shellPath?: string;
	/** Dialect for generated internal-URL path literals. Defaults to POSIX; does not select the executable. */
	shellDialect?: "posix" | "powershell";
	/** Last-mile hook for rewriting the command/cwd/env spawn context. */
	spawnHook?: BashSpawnHook;
	/** Optional command interceptor used by extensions and parity tests. */
	interceptor?: BashInterceptor;
	interceptorEnabled?: boolean | (() => boolean);
	availableTools?: string[];
	interceptorRules?: BashInterceptorRule[];
	/**
	 * Session-scoped directory for overflow logs, resolved per execution so a
	 * session switch (fork, branch, resume) follows the live transcript session.
	 */
	sessionTempDir?: string | (() => string | undefined);
}
/**
 * Presentation identity for a shell-backed built-in tool. Atomic runs PowerShell
 * through the same execution pipeline as bash, so the transcript prompt and the
 * overflow temp-file prefix are the only shell-specific display values.
 */
export interface ShellToolPresentation {
	/** Transcript prompt prefix: `$` for bash, `PS>` for PowerShell. */
	prompt: string;
	/** Prefix for the temp file that holds truncated output. */
	tempFilePrefix: string;
}
export const BASH_SHELL_PRESENTATION: ShellToolPresentation = {
	prompt: "$",
	tempFilePrefix: `${APP_NAME}-bash`,
};
const BASH_UPDATE_THROTTLE_MS = 100;
function bashResultToToolResult(result: BashResult): {
	content: Array<{ type: "text"; text: string }>;
	details: BashToolDetails | undefined;
} {
	const details: BashToolDetails | undefined = result.truncated
		? { fullOutputPath: result.fullOutputPath }
		: undefined;
	const status = result.cancelled
		? "Command aborted"
		: result.exitCode && result.exitCode !== 0
			? `Command exited with code ${result.exitCode}`
			: undefined;
	const text = `${result.output || "(no output)"}${status ? `\n\n${status}` : ""}`;
	return { content: [{ type: "text", text }], details };
}
function formatBashCall(
	args: { command?: string; timeout?: number; action?: string; id?: string } | undefined,
	prompt: string,
): string {
	if (args?.action === "wait") return theme.fg("toolTitle", theme.bold(`${prompt} wait ${str(args.id) ?? "..."}`));
	const command = str(args?.command);
	const timeout = args?.timeout as number | undefined;
	const timeoutSuffix = timeout ? theme.fg("muted", ` (timeout ${timeout}s)`) : "";
	const commandDisplay = command === null ? invalidArgText(theme) : command ? command : theme.fg("toolOutput", "...");
	return theme.fg("toolTitle", theme.bold(`${prompt} ${commandDisplay}`)) + timeoutSuffix;
}
function rebuildBashResultRenderComponent(
	component: Container,
	result: {
		content: Array<{ type: string; text?: string; data?: string; mimeType?: string }>;
		details?: BashToolDetails;
	},
	options: ToolRenderResultOptions,
	showImages: boolean,
	timerText: string | undefined,
): void {
	component.clear();
	let output = getTextOutput(result, showImages).trim();
	const observation = result.details?.observation;
	if (observation?.kind === "yielded") {
		const receipt = `Command is still running (task ${observation.taskId}).`;
		if (output.endsWith(receipt)) output = output.slice(0, -receipt.length).trimEnd();
		component.addChild(new Text(theme.fg("accent", "∀ Continued in background"), 0, 0));
		component.addChild(
			new Text(
				theme.fg("dim", `/tasks to inspect output or stop${options.expanded ? ` · ${observation.taskId}` : ""}`),
				0,
				0,
			),
		);
	}
	const truncation = result.details?.truncation;
	const fullOutputPath = result.details?.fullOutputPath;
	if (!options.isPartial && truncation?.truncated && fullOutputPath && output.endsWith("]")) {
		const footerStart = output.lastIndexOf("\n\n[");
		if (footerStart !== -1 && output.slice(footerStart).includes(fullOutputPath)) {
			output = output.slice(0, footerStart).trimEnd();
		}
	}
	if (output) addShellOutputPreview(component, output, options.expanded);
	if (truncation?.truncated || fullOutputPath) {
		const warnings: string[] = [];
		if (fullOutputPath) {
			warnings.push(`Full output: ${fullOutputPath}`);
		}
		if (truncation?.truncated) warnings.push(formatTruncationNotice(truncation));
		component.addChild(new Text(`\n${theme.fg("warning", `[${warnings.join(". ")}]`)}`, 0, 0));
	}
	if (timerText) component.addChild(new Text(`\n${theme.fg("muted", timerText)}`, 0, 0));
}
export function createBashToolDefinition(
	cwd: string,
	options?: BashToolOptions,
	presentation: ShellToolPresentation = BASH_SHELL_PRESENTATION,
): ToolDefinition<typeof bashSchema, BashToolDetails | undefined, ToolTimerState> {
	const defaultOps =
		options?.operations ??
		createLocalBashOperations({ shellPath: options?.shellPath, taskOwner: options?.taskOwner });
	const commandPrefix = options?.commandPrefix,
		exposeSessionEnvironment = options?.exposeSessionEnvironment ?? true;
	const spawnHook = options?.spawnHook;
	const interceptor = options?.interceptor;
	const isInterceptorEnabled = (): boolean =>
		typeof options?.interceptorEnabled === "function"
			? options.interceptorEnabled()
			: (options?.interceptorEnabled ?? !!interceptor);
	const availableTools = options?.availableTools ?? ["read", "search", "find", "edit", "write"];
	const interceptorRules = options?.interceptorRules ?? DEFAULT_BASH_INTERCEPTOR_RULES;
	const sessionTempDirOption = options?.sessionTempDir;
	const resolveSessionTempDir = (): string | undefined =>
		typeof sessionTempDirOption === "function" ? sessionTempDirOption() : sessionTempDirOption;
	return {
		name: "bash",
		concurrency: (args) => (args.pty === true ? "exclusive" : "shared"),
		label: "bash",
		description:
			'Execute a shell command with optional PTY handling and foreground/background observation, or observe an existing task with action: "wait", id, and optional budgetMs. Omitted command wait uses owner policy (default 10s). Observation never changes execution timeout. Background and existing-task waits require a supported task owner; unbound foreground execution waits until completion.',
		promptSnippet: bashToolSystemPromptContribution.snippet,
		constrainedSampling: { type: "json_schema", strict: "prefer" },
		promptGuidelines: exposeSessionEnvironment ? [...bashToolSystemPromptContribution.guidelines] : undefined,
		prepareArguments: prepareShellInput,
		parameters: bashSchema,
		outputSchema: bashOutputSchema,
		maxResultSizeChars: Infinity,
		async execute(_toolCallId, bashCommand: BashToolInput, signal?: AbortSignal, onUpdate?, ctx?: ExtensionContext) {
			if (bashCommand.action !== undefined) {
				if (
					bashCommand.action !== "wait" ||
					typeof bashCommand.id !== "string" ||
					Object.keys(bashCommand).some((key) => !["action", "id", "budgetMs"].includes(key)) ||
					(bashCommand.budgetMs !== undefined &&
						(typeof bashCommand.budgetMs !== "number" ||
							!Number.isFinite(bashCommand.budgetMs) ||
							bashCommand.budgetMs < 0))
				)
					throw new Error(
						"Invalid shell wait: expected action wait, id, and optional finite non-negative budgetMs",
					);
				return waitForSupervisedCommand(options?.taskOwner, bashCommand.id, bashCommand.budgetMs, signal, {
					tempFilePrefix: presentation.tempFilePrefix,
					tempDir: resolveSessionTempDir(),
				});
			}
			if (
				bashCommand.id !== undefined ||
				bashCommand.budgetMs !== undefined ||
				typeof bashCommand.command !== "string"
			)
				throw new Error("Invalid shell command: command is required; id and budgetMs require action wait");
			const { command } = bashCommand;
			const timeout = normalizeTimeoutSeconds(bashCommand.timeout);
			validateBashWait(bashCommand.wait, !!options?.operations || !!options?.taskOwner);
			const sessionEnvironment = snapshotBashSessionEnvironment(ctx, exposeSessionEnvironment);
			const resourceCtx = ctx as InternalResourceContext | undefined;
			const executionCwd = ctx?.cwd || cwd;
			const hasExplicitCwd = typeof bashCommand.cwd === "string";
			const rawStrippedContext = hasExplicitCwd ? undefined : stripLeadingCdCommand(command, executionCwd);
			const interceptorEnabled = isInterceptorEnabled();
			if (interceptorEnabled)
				checkBashInterceptionCandidates([command, rawStrippedContext?.command], availableTools, interceptorRules);
			const cwdInput = hasExplicitCwd
					? await expandShellInternalUrls(bashCommand.cwd!, executionCwd, resourceCtx)
					: executionCwd,
				requestedCwd = resolvePath(executionCwd, cwdInput);
			const expandedCommand = await expandShellInternalUrls(
				command,
				executionCwd,
				resourceCtx,
				true,
				options?.shellDialect,
			);
			const strippedExpandedContext = hasExplicitCwd
				? undefined
				: stripLeadingCdCommand(expandedCommand, requestedCwd);
			const resolvedCommand = commandPrefix ? `${commandPrefix}\n${expandedCommand}` : expandedCommand;
			const spawnContext = resolveSpawnContext(resolvedCommand, requestedCwd, sessionEnvironment, spawnHook);
			const strippedCdContext = strippedExpandedContext
				? resolveSpawnContext(
						commandPrefix
							? `${commandPrefix}\n${strippedExpandedContext.command}`
							: strippedExpandedContext.command,
						strippedExpandedContext.cwd,
						sessionEnvironment,
						spawnHook,
					)
				: undefined;
			if (interceptorEnabled)
				checkBashInterceptionCandidates(
					[
						expandedCommand,
						strippedExpandedContext?.command,
						resolvedCommand,
						spawnContext.command,
						strippedCdContext?.command,
					],
					availableTools,
					interceptorRules,
				);
			let expandedEnv: NodeJS.ProcessEnv | undefined;
			if (bashCommand.env) {
				expandedEnv = {};
				for (const [key, value] of Object.entries(bashCommand.env)) {
					if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(key)) throw new Error(`Invalid bash env name: ${key}`);
					expandedEnv[key] = await expandShellInternalUrls(value, executionCwd, resourceCtx);
				}
				spawnContext.env = { ...spawnContext.env, ...expandedEnv };
			}
			if (strippedCdContext && expandedEnv) strippedCdContext.env = { ...strippedCdContext.env, ...expandedEnv };
			applyBashSessionEnvironment(spawnContext.env, sessionEnvironment);
			if (strippedCdContext) applyBashSessionEnvironment(strippedCdContext.env, sessionEnvironment);
			const primaryInterception = interceptorEnabled && interceptor ? await interceptor(spawnContext) : undefined;
			const fallbackInterception =
				!primaryInterception && strippedCdContext && interceptorEnabled && interceptor
					? await interceptor(strippedCdContext)
					: undefined;
			const intercepted = primaryInterception ?? fallbackInterception;
			if (intercepted?.result) return bashResultToToolResult(intercepted.result);
			const ops = intercepted?.operations ?? defaultOps;
			const executionContext = primaryInterception ? spawnContext : (strippedCdContext ?? spawnContext);
			const output = new OutputAccumulator({
				tempFilePrefix: presentation.tempFilePrefix,
				tempDir: resolveSessionTempDir(),
			});
			let acceptingOutput = true;
			let updateTimer: NodeJS.Timeout | undefined;
			let updateDirty = false;
			let lastUpdateAt = 0;
			const emitOutputUpdate = () => {
				if (!onUpdate || !updateDirty) return;
				updateDirty = false;
				lastUpdateAt = Date.now();
				const snapshot = output.snapshot({ persistIfTruncated: true });
				onUpdate({
					content: [{ type: "text", text: snapshot.content || "" }],
					details: {
						truncation: snapshot.truncation.truncated ? snapshot.truncation : undefined,
						fullOutputPath: snapshot.fullOutputPath,
					},
				});
			};
			const clearUpdateTimer = () => {
				if (updateTimer) {
					clearTimeout(updateTimer);
					updateTimer = undefined;
				}
			};
			const scheduleOutputUpdate = () => {
				if (!onUpdate) return;
				updateDirty = true;
				const delay = BASH_UPDATE_THROTTLE_MS - (Date.now() - lastUpdateAt);
				if (delay <= 0) {
					clearUpdateTimer();
					emitOutputUpdate();
					return;
				}
				updateTimer ??= setTimeout(() => {
					updateTimer = undefined;
					emitOutputUpdate();
				}, delay);
			};
			if (onUpdate) {
				onUpdate({ content: [], details: undefined });
			}
			const handleData = (data: Buffer) => {
				if (!acceptingOutput) return;
				output.append(data);
				scheduleOutputUpdate();
			};
			const finishOutput = async () => {
				acceptingOutput = false;
				output.finish();
				clearUpdateTimer();
				emitOutputUpdate();
				const snapshot = output.snapshot({ persistIfTruncated: true });
				try {
					await output.closeTempFile();
				} catch {
					// The spill file could not be flushed. Keep the truncated result, but
					// re-read the snapshot so the now-cleared path is not advertised.
					return output.snapshot();
				}
				return snapshot;
			};
			const formatOutput = (snapshot: Awaited<ReturnType<typeof finishOutput>>, emptyText = "(no output)") => {
				const truncation = snapshot.truncation;
				let text = snapshot.content || emptyText;
				let details: BashToolDetails | undefined;
				if (truncation.truncated) {
					details = { truncation, fullOutputPath: snapshot.fullOutputPath };
					const startLine = truncation.totalLines - truncation.outputLines + 1;
					const endLine = truncation.totalLines;
					// Persistence can be refused (a temp directory we will not write
					// through, a full or read-only disk), and then there is no path to
					// name. Every other renderer already omits the clause; this one must
					// too, rather than handing the model the string "undefined".
					const fullOutputNote = snapshot.fullOutputPath ? ` Full output: ${snapshot.fullOutputPath}` : "";
					if (truncation.lastLinePartial) {
						const lastLineSize = formatSize(output.getLastLineBytes());
						text += `\n\n[Showing last ${formatSize(truncation.outputBytes)} of line ${endLine} (line is ${lastLineSize}).${fullOutputNote}]`;
					} else if (truncation.truncatedBy === "lines") {
						text += `\n\n[Showing lines ${startLine}-${endLine} of ${truncation.totalLines}.${fullOutputNote}]`;
					} else {
						text += `\n\n[Showing lines ${startLine}-${endLine} of ${truncation.totalLines} (${formatSize(DEFAULT_MAX_BYTES)} limit).${fullOutputNote}]`;
					}
				}
				return { text, details };
			};
			const startedAt = Date.now();
			const withTiming = (details: BashToolDetails | undefined): BashToolDetails => ({
				...details,
				timeoutSeconds: timeout,
				...(bashCommand.timeout !== undefined && bashCommand.timeout !== timeout
					? { requestedTimeoutSeconds: bashCommand.timeout }
					: {}),
				wallTimeMs: Date.now() - startedAt,
			});
			const appendStatus = (text: string, status: string) => `${text ? `${text}\n\n` : ""}${status}`;
			try {
				let exitCode: number | null;
				let observation: SupervisedCommandResult["observation"];
				try {
					const result = await ops.exec(executionContext.command, executionContext.cwd, {
						onData: handleData,
						signal,
						timeout,
						wait: bashCommand.wait,
						env: executionContext.env,
						pty: bashCommand.pty,
					});
					exitCode = result.exitCode;
					observation = result.observation;
				} catch (err) {
					const snapshot = await finishOutput();
					const { text } = formatOutput(snapshot, "");
					if (err instanceof Error && err.message === "aborted") {
						throw new Error(appendStatus(text, "Command aborted"));
					}
					if (err instanceof Error && err.message.startsWith("timeout:")) {
						const timeoutSecs = err.message.split(":")[1];
						throw new Error(appendStatus(text, `Command timed out after ${timeoutSecs} seconds`));
					}
					throw err;
				}
				const snapshot = await finishOutput();
				const { text: outputText, details } = formatOutput(snapshot);
				if (observation?.kind === "yielded")
					return {
						content: [
							{
								type: "text",
								text: appendStatus(outputText, `Command is still running (task ${observation.taskId}).`),
							},
						],
						details: { ...withTiming(details), observation },
					};
				if (exitCode === null) {
					throw new Error(appendStatus(outputText, "Command terminated without an exit code"));
				}
				const fullOutput = await output.readFullOutput(STRUCTURED_OUTPUT_MAX_BYTES);
				const structuredContent = {
					output: fullOutput.content,
					truncated: fullOutput.truncated,
					...(fullOutput.truncated && snapshot.fullOutputPath
						? { full_output_path: snapshot.fullOutputPath }
						: {}),
					exit_code: exitCode,
					wall_time_seconds: Math.round((Date.now() - startedAt) / 100) / 10,
				};
				if (exitCode !== 0) {
					return {
						content: [{ type: "text", text: appendStatus(outputText, `Command exited with code ${exitCode}`) }],
						details: { ...withTiming(details), exitCode },
						isError: true,
						structuredContent,
					};
				}
				return { content: [{ type: "text", text: outputText }], details: withTiming(details), structuredContent };
			} finally {
				invalidateNativeSearchCache();
				clearUpdateTimer();
			}
		},
		renderCall(args, _theme, context) {
			startToolTimer(context);
			const text = (context.lastComponent as Text | undefined) ?? new Text("", 0, 0);
			text.setText(formatBashCall(args, presentation.prompt));
			return text;
		},
		renderResult(result, options, _theme, context) {
			// A command yielded to the background is still running, so its row reports how long it was observed.
			const finalLabel = result.details?.observation?.kind === "yielded" ? "Observed for" : "Took";
			const timerText = toolTimerText(context, options.isPartial, finalLabel);
			const component = (context.lastComponent as Container | undefined) ?? new Container();
			const waited =
				context.args.action === "wait" &&
				rebuildShellWaitResult(component, result, options, {
					showImages: context.showImages,
					isError: context.isError,
					taskId: context.args.id,
					timerText,
				});
			if (!waited) rebuildBashResultRenderComponent(component, result, options, context.showImages, timerText);
			component.invalidate();
			return component;
		},
	};
}
export function createBashTool(cwd: string, options?: BashToolOptions): AgentTool<typeof bashSchema> {
	return wrapToolDefinition(createBashToolDefinition(cwd, options));
}
